import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Cpu, BarChart2, Database, Shield, FileText, User, Merge,
  Brain, FolderOpenDot, Layers3, Settings, Clock,
  ChevronRight,
  AppWindow,
  MessageSquare,
  UserRound,
  MessageCircle,
  LayoutDashboard,
  ShieldCheck,
  FileBarChart,
  Scale,
  Activity,
} from 'lucide-react';
import { useTheme } from './Theme';
import SidebarFooter from './SidebarFooter';
import SidebarBrand from './SidebarBrand';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RoutePrivilege {
  id: string;
  hasAccess: boolean;
  children?: RoutePrivilege[];
}

interface NavItem {
  to: string;
  icon: any;
  label: string;
  id: string;
  children?: NavItem[];
}

const SIDEBAR_SECTIONS_STORAGE_KEY = 'portal_sidebar_collapsed_sections';
const DEFAULT_EXPANDED_SECTION = 'Platform';

function createDefaultCollapsedSections(): Set<string> {
  return new Set([
    'Configurations',
    'MCP Access Manager',
    'AI Governance',
    'Access & System',
    'Monitoring',
  ]);
}

function readCollapsedSectionsFromStorage(): Set<string> | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((s): s is string => typeof s === 'string'));
  } catch {
    return null;
  }
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const iconSize = collapsed ? 16 : 14;
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => readCollapsedSectionsFromStorage() ?? createDefaultCollapsedSections()
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(Array.from(collapsedSections)));
  }, [collapsedSections]);

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const isSectionExpanded = (section: string) => !collapsedSections.has(section);

  const navLinkClass = (isActive: boolean) =>
    `flex items-center ${collapsed ? 'justify-center' : 'gap-1.5'} px-2 py-1.5 rounded-md transition-all duration-150 text-xs font-medium border-l-2 ${collapsed ? 'border-l-transparent' : ''} ${isActive
      ? (isLight ? 'bg-indigo-50 text-indigo-700 border-l-indigo-500' : 'bg-neutral-800 text-white border-l-indigo-500')
      : (isLight ? 'border-l-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900' : 'border-l-transparent text-gray-300 hover:bg-neutral-800/70 hover:text-white')
    }`;

  const routeAccess: RoutePrivilege[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_routes') || '[]');
    } catch {
      return [];
    }
  }, []);

  const hasAccess = (id: string, routes: RoutePrivilege[] = routeAccess): boolean => {
    for (const route of routes) {
      if (route.id === id) return route.hasAccess ?? false;
      if (Array.isArray(route.children) && route.children.length > 0) {
        const found = hasAccess(id, route.children);
        if (found) return true;
      }
    }
    return false;
  };

  const renderNavLink = (item: NavItem) => {
    if (!hasAccess(item.id)) return null;

    return (
      <div key={item.to}>
        <NavLink
          to={item.to}
          className={({ isActive }) => navLinkClass(isActive)}
          title={collapsed ? item.label : undefined}
        >
          <item.icon size={iconSize} className="shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>

        {/* Render nested children */}
        {item.children && item.children.length > 0 && (
          <div className={`${collapsed ? '' : 'ml-3 pl-2'} border-l ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            {item.children.map((child) => renderNavLink(child))}
          </div>
        )}
      </div>
    );
  };

  const navItems: { section: string; links: NavItem[] }[] = [

    {
      section: 'Platform',
      links: [
        { to: '/portal/workflows', icon: Cpu, label: 'Agent Orchestrations', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
        { to: '/portal/applications', icon: AppWindow, label: 'Applications', id: '6f3a0f4c-20b7-4c2a-b4c1-7caaed2358b7' },
        { to: '/portal/chatagent', icon: MessageCircle, label: 'Copilots', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
        { to: '/portal/agents', icon: Brain, label: 'Functions', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
        { to: '/portal/knowledge-base', icon: Layers3, label: 'Knowledge Workspace', id: 'h8i9j0k1-l2m3-4567-2345-678901234567' },
        { to: '/portal/analytics-hub', icon: FileBarChart, label: 'Analytics Hub', id: '70b4dff6-7d13-43b0-a6d6-cba6b5b4a8ce' },
      ],
    },
    {
      section: 'Configurations',
      links: [
        { to: '/portal/configurations/data-ingestion', icon: FolderOpenDot, label: 'Data Models', id: 'f6g7h8i9-j0k1-2345-f123-456789012345' },
        { to: '/portal/configurations/llm-routing', icon: Merge, label: 'LLM Routing', id: 'k1l2m3n4-o5p6-7890-5678-901234567890' },
        { to: '/portal/configurations/data-vault', icon: Database, label: 'Data Vault', id: 'o5p6q7r8-s9t0-1234-9012-345678901234' },
        { to: '/portal/configurations/schedules', icon: Clock, label: 'Schedules', id: 's9t0u1v2-w3x4-5678-3456-789012345678' },
      ],
    },
    {
      section: 'MCP Access Manager',
      links: [
        { to: '/portal/mcpdashboard', icon: LayoutDashboard, label: 'MCP Dashboard', id: 'a1d2f3b4-5c6e-47a8-9b01-2c3d4e5f6a7b' },
        { to: '/portal/mcpdevelopers', icon: ShieldCheck, label: 'Resource Access', id: 'b2e3c4d5-6f7a-48b9-0c12-3d4e5f6a7b8c' },
        { to: '/portal/mcpresources', icon: Database, label: 'MCP Resources', id: 'c3f4d5e6-7a8b-49c0-1d23-4e5f6a7b8c9d' },
      ],
    },
    {
      section: 'AI Governance',
      links: [
        { to: '/portal/ai-governance/policies', icon: Scale, label: 'AI Governance', id: 'g1h2i3j4-k5l6-7890-abcd-ef12345678901' },
      ],
    },
    {
      section: 'Access & System',
      links: [
        { to: '/portal/connections', icon: Cpu, label: 'Connections & Keys', id: 'e1f2g3h4-i5j6-7890-5678-901234567890' },
        { to: '/portal/access-settings', icon: Shield, label: 'Access Settings', id: 'h4i5j6k7-l8m9-0123-8901-234567890123' },
        { to: '/portal/system-settings', icon: Settings, label: 'System Settings', id: 'm9n0o1p2-q3r4-5678-3456-789012345678' },
        { to: '/portal/my-account', icon: User, label: 'My Account', id: 'n0o1p2q3-r4s5-6789-4567-890123456789' },
      ],
    },
    {
      section: 'Monitoring',
      links: [
        { to: '/portal/logs', icon: FileText, label: 'AI Observability', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
        { to: '/portal/health-status', icon: Activity, label: 'System Health', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
      ],
    },
  ];

  useEffect(() => {
    navItems.forEach(({ section, links }) => {
      const hasActiveLink = links.some(
        (link) => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`)
      );
      if (hasActiveLink) {
        setCollapsedSections((prev) => {
          if (!prev.has(section)) return prev;
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }
    });
  }, [location.pathname]);

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-48'
        } ${isLight ? 'bg-white border-r border-neutral-200' : 'bg-neutral-950 border-r border-neutral-800'} fixed top-0 bottom-0 left-0 z-40 flex flex-col transition-all duration-200`}
    >
      <div className={`shrink-0 border-b ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
        <SidebarBrand collapsed={collapsed} onClick={() => navigate('/home')} />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden pt-2">
        <div className="px-2 pb-2">
          {hasAccess('a1b2c3d4-e5f6-7890-abcd-ef1234567890') && (
            <NavLink to="/portal/home" className={({ isActive }) => navLinkClass(isActive)}>
              <Home size={iconSize} className="shrink-0" />
              {!collapsed && <span className="truncate">Home</span>}
            </NavLink>
          )}
        </div>

        <nav className="space-y-0.5 px-1.5">
          {navItems.map((section) => {
            const visibleLinks = section.links.filter((item) => hasAccess(item.id));
            if (visibleLinks.length === 0) return null;

            return (
              <div key={section.section}>
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.section)}
                    className={`w-full flex items-center justify-between gap-1 mt-3 mb-0.5 px-1.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-colors first:mt-1 ${isLight
                      ? 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50'
                      : 'text-gray-500 hover:text-gray-300 hover:bg-neutral-800/50'
                      }`}
                    aria-expanded={isSectionExpanded(section.section)}
                    aria-label={`${isSectionExpanded(section.section) ? 'Collapse' : 'Expand'} ${section.section}`}
                  >
                    <span className="truncate text-left">{section.section}</span>
                    <ChevronRight
                      size={12}
                      strokeWidth={2.5}
                      className={`shrink-0 transition-transform duration-200 ease-out ${isSectionExpanded(section.section) ? 'rotate-90' : ''}`}
                    />
                  </button>
                )}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isSectionExpanded(section.section) || collapsed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                >
                  <div className="overflow-hidden space-y-0.5">
                    {visibleLinks.map(renderNavLink)}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <SidebarFooter collapsed={collapsed} setCollapsed={setCollapsed} />
    </aside>
  );
};

export default Sidebar;



// import React, { useState } from 'react';
// import { NavLink } from 'react-router-dom';
// import {
//   Home,
//   Cpu,
//   BarChart2,
//   Database,
//   Shield,
//   FileText,
//   User,
//   Merge,
//   Server,
//   Brain,
//   FolderOpenDot,
//   Layers3,
//   Settings,
//   Clock,
//   BrainCog,
//   ChevronLeft,
//   ChevronRight,
//   PanelLeftOpen,
//   PanelRightOpen,
// } from 'lucide-react';

// interface SidebarProps {
//   collapsed: boolean;
//   setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
// }

// const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {

//   const linkClass = ({ isActive }: any) =>
//   `flex items-center ${
//     collapsed ? 'justify-center' : 'gap-2'
//   } px-3 py-2 rounded-md transition-all text-[13px] font-medium ${
//     isActive
//       ? 'bg-neutral-800 text-white'
//       : 'text-gray-200 hover:bg-neutral-800 hover:text-white'
//   }`;

//   const iconSize = collapsed ? 20 : 16;
//   const navItems = [
//     {
//       section: 'Platform',
//       links: [
//         { to: '/portal/workflows', icon: Cpu, label: 'Orchestrations' ,id:'b2c3d4e5-f6g7-8901-bcde-f12345678901'},
//         { to: '/portal/agents', icon: Brain, label: 'Functions' ,id:'d4e5f6g7-h8i9-0123-def1-234567890123'},
//       ],
//     },
//     {
//       section: 'Configurations',
//       id:'',
//       links: [
//         { to: '/portal/configurations/data-ingestion', icon: FolderOpenDot, label: 'Entities',id:'f6g7h8i9-j0k1-2345-f123-456789012345' },
//         { to: '/portal/knowledge-base', icon: Layers3, label: 'Knowledge Workspace',id:'h8i9j0k1-l2m3-4567-2345-678901234567' },
//         { to: '/portal/configurations/llm-routing', icon: Merge, label: 'LLM Routing' ,id:'k1l2m3n4-o5p6-7890-5678-901234567890'},
//         { to: '/portal/configurations/data-vault', icon: Database, label: 'Data Vault',id:'o5p6q7r8-s9t0-1234-9012-345678901234' },
//         { to: '/portal/configurations/schedules', icon: Clock, label: 'Schedules',id:'s9t0u1v2-w3x4-5678-3456-789012345678' },
//         { to: '/portal/intent-orchestration', icon: BrainCog, label: 'Intent Orchestration' ,id:'w3x4y5z6-a7b8-9012-7890-123456789012'},
//         { to: '/portal/deployments', icon: Server, label: 'Deployments' ,id:'a7b8c9d0-e1f2-3456-1234-567890123456'},
//       ],
//     },
//     {
//       section: 'Access & System',
//       links: [
//         { to: '/portal/connections', icon: Cpu, label: 'Connections & Keys',id:'e1f2g3h4-i5j6-7890-5678-901234567890' },
//         { to: '/portal/access-settings', icon: Shield, label: 'Access Settings',id:'h4i5j6k7-l8m9-0123-8901-234567890123' },
//         { to: '/portal/system-settings', icon: Settings, label: 'System Settings' ,id:'m9n0o1p2-q3r4-5678-3456-789012345678'},
//         { to: '/portal/my-account', icon: User, label: 'My Account',id:'n0o1p2q3-r4s5-6789-4567-890123456789' },
//       ],
//     },
//     {
//       section: 'Monitoring',
//       links: [
//         { to: '/portal/reports', icon: BarChart2, label: 'Reports',id:'p2q3r4s5-t6u7-8901-6789-012345678901' },
//         { to: '/portal/logs', icon: FileText, label: 'Logs',id:'o1p2q3r4-s5t6-7890-5678-901234567890' },
//       ],
//     },
//   ];

//   const toggleSidebar = () => setCollapsed((prev) => !prev);

//   return (
//    <aside
//   className={`${
//     collapsed ? 'w-20' : 'w-56'
//   } bg-neutral-950 border-r border-neutral-800 pt-4 fixed top-16 bottom-0 left-0 flex flex-col transition-all duration-200`}
// >
//   {/* Scrollable nav content */}
//   <div className="flex-1 overflow-y-auto overflow-x-hidden">
//     {/* Home Link */}
//     <div className="px-3 pb-4">
//       <NavLink to="/portal/home" className={linkClass}>
//         <Home size={iconSize} />
//         {!collapsed && <span>Home</span>}
//       </NavLink>
//     </div>

//     {/* Navigation sections */}
//     <nav className="space-y-1 px-2">
//       {navItems.map((section) => (
//         <div key={section.section}>
//           {!collapsed && (
//             <div className="text-[11px] text-gray-400 uppercase tracking-wide mt-4 mb-2 px-2">
//               {section.section}
//             </div>
//           )}
//           {section.links.map(({ to, icon: Icon, label }) => (
//             <NavLink
//               key={to}
//               to={to}
//               className={linkClass}
//               title={collapsed ? label : undefined}
//             >
//               <Icon size={iconSize} />
//               {!collapsed && <span>{label}</span>}
//             </NavLink>
//           ))}
//         </div>
//       ))}
//     </nav>
//   </div>

//   {/* Collapse/Expand Toggle - Fixed at bottom */}
//   <div className="border-t border-neutral-800 p-2 flex justify-center">
//     <button
//       onClick={toggleSidebar}
//       className="text-gray-400 hover:text-white transition"
//       title={collapsed ? 'Expand' : 'Collapse'}
//     >
//       {collapsed ? <PanelLeftOpen size={20} /> : <PanelRightOpen size={20} />}

//     </button>
//   </div>
// </aside>

//   );
// };

// export default Sidebar;
